import { ApiResponse, parseResult } from '@parcel2go/data';
import { getLogger } from '@parcel2go/logging';
import {
  ActionType,
  GetInstructionsApiResponseModel,
  Integration,
  LinkAccountResponseApiModel,
  SettingsApiResponseModel,
  StrVal,
} from './types';
import { currentAuth, User } from '@parcel2go/authentication';
import { GetBaseUrl } from '../helpers/GetBaseUrl';

const logger = getLogger();

function getPath(profile: string, path: string) {
  return `${process.env.REACT_APP_API}/${profile}/v1/${path}`;
}

const getAccessToken = async (): Promise<string> => {
  let user: User | null = await currentAuth.getUser();
  if (user && user.access_token && !user.expired) {
    return user.access_token;
  } else if (user) {
    user = await currentAuth.renewToken();
    return user.access_token;
  } else {
    throw new Error('user not signed in');
  }
};

const authedFetch = async (url: string, settings?: RequestInit, tokenOverride?: string): Promise<Response> => {
  let token: string = tokenOverride || (await getAccessToken());

  const defaultSettings: RequestInit = {
    method: 'GET',
    headers: new Headers({
      Authorization: 'Bearer ' + token,
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': 'true',
      'X-SOCKET-ID': localStorage?.getItem('WEBSOCKET:SESSIONID') || '',
    }),
  };

  let combinedSettings: RequestInit = { ...defaultSettings, ...settings };

  return fetch(url, combinedSettings);
};

export async function getintegrationsummary(profile: string): Promise<ApiResponse<Integration[]>> {
  const url = getPath(profile, `integrations`);
  const response = await authedFetch(url, { method: 'GET' });
  return parseResult<Integration[]>(response);
}

export async function postInternalAuthCallbackUrl(
  internalAuthCallbackUrl: string,
): Promise<ApiResponse<{ redirectUrl: string }>> {
  const response = await authedFetch(internalAuthCallbackUrl, {
    method: 'POST',
    body: JSON.stringify({
      ReturnUrl: GetBaseUrl(),
    }),
  });
  return parseResult<{ redirectUrl: string }>(response);
}

export async function getActionInstructions(
  profile: string,
  integrationsType: string,
  slug: string,
  action: ActionType,
  accountId?: string,
): Promise<ApiResponse<GetInstructionsApiResponseModel>> {
  const url = !!accountId
    ? getPath(profile, `integrations/${integrationsType}/${slug}/${action}/instructions/${accountId}`)
    : getPath(profile, `integrations/${integrationsType}/${slug}/${action}/instructions`);
  const response = await authedFetch(url, { method: 'GET' });
  return parseResult<GetInstructionsApiResponseModel>(response);
}

export async function getSettings(profile: string): Promise<ApiResponse<SettingsApiResponseModel>> {
  try {
    const url = getPath(profile, `setup`);
    const response = await authedFetch(url, {}, '');
    return parseResult<SettingsApiResponseModel>(response);
  } catch {
    return { success: false } as ApiResponse<any>;
  }
}

export async function postActionValues(
  profile: string,
  integrationsType: string,
  slug: string,
  action: ActionType,
  model: StrVal,
  accountId?: string,
): Promise<ApiResponse<LinkAccountResponseApiModel>> {
  // Add return URL for Links and Renews
  if (action == 'Link' || action == 'Renew') {
    model['ReturnUrl'] = GetBaseUrl();
  }

  const url = !!accountId
    ? getPath(profile, `integrations/${integrationsType}/${slug}/${action}/${accountId}`)
    : getPath(profile, `integrations/${integrationsType}/${slug}/${action}`);
  const response = await authedFetch(url, { method: 'POST', body: JSON.stringify(model) });
  return parseResult<LinkAccountResponseApiModel>(response);
}

export type { ApiResponse };
